import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { UsersPartyPermissionsReadType } from '@portal/api/enums/users-party-permissions-read-type';

@Injectable({
    providedIn: 'root'
})
export class EventNavigationService {
    private _eventNavigationItems = new BehaviorSubject<FuseNavigationItem[]>([]);
    private _currentEventId: string | null = null;
    private _currentEventName: string | null = null;

    /**
     * Getter for event navigation items
     */
    get eventNavigationItems$(): Observable<FuseNavigationItem[]> {
        return this._eventNavigationItems.asObservable();
    }

    /**
     * Set event navigation items based on event data
     */
    setEventNavigation(eventId: string, eventName: string, eventData: any): void {
        this._currentEventId = eventId;
        this._currentEventName = eventName;

        const eventNavigationItem: FuseNavigationItem = {
            id: `current-event-${eventId}`,
            title: eventName,
            subtitle: 'Gestão do evento',
            type: 'group',
            children: this.buildEventTabItems(eventId, eventData)
        };

        this._eventNavigationItems.next([eventNavigationItem]);
    }

    /**
     * Clear event navigation items
     */
    clearEventNavigation(): void {
        this._currentEventId = null;
        this._currentEventName = null;
        this._eventNavigationItems.next([]);
    }

    /**
     * Get current event ID
     */
    getCurrentEventId(): string | null {
        return this._currentEventId;
    }

    /**
     * Get current event name
     */
    getCurrentEventName(): string | null {
        return this._currentEventName;
    }

    /**
     * Build event tab items based on event data
     */
    private buildEventTabItems(eventId: string, eventData: any): FuseNavigationItem[] {
        const tabItems: FuseNavigationItem[] = [];

        // Dashboard (always available)
        tabItems.push({
            id: `event-${eventId}-dashboard`,
            title: 'Painel',
            type: 'basic',
            icon: 'heroicons_outline:chart-bar',
            link: `/party/events/${eventId}/dashboard`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Income/Receitas
        tabItems.push({
            id: `event-${eventId}-income`,
            title: 'Receitas',
            type: 'basic',
            icon: 'heroicons_outline:currency-euro',
            link: `/party/events/${eventId}/income`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Expenses/Despesas
        tabItems.push({
            id: `event-${eventId}-expenses`,
            title: 'Despesas',
            type: 'basic',
            icon: 'heroicons_outline:currency-euro',
            link: `/party/events/${eventId}/expenses`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Offers/Ofertas
        tabItems.push({
            id: `event-${eventId}-offers`,
            title: 'Ofertas',
            type: 'basic',
            icon: 'heroicons_outline:gift',
            link: `/party/events/${eventId}/offers`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Sponsors/Patrocinadores
        tabItems.push({
            id: `event-${eventId}-sponsors`,
            title: 'Patrocinadores',
            type: 'basic',
            icon: 'heroicons_outline:user-group',
            link: `/party/events/${eventId}/sponsors`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Donations/Donativos
        tabItems.push({
            id: `event-${eventId}-donations`,
            title: 'Donativos',
            type: 'basic',
            icon: 'heroicons_outline:heart',
            link: `/party/events/${eventId}/donations`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Bar
        tabItems.push({
            id: `event-${eventId}-bar`,
            title: 'Postos de Venda',
            type: 'basic',
            icon: 'heroicons_outline:shopping-bag',
            link: `/party/events/${eventId}/bar`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Registrations/Inscrições
        tabItems.push({
            id: `event-${eventId}-registrations`,
            title: 'Inscrições',
            type: 'basic',
            icon: 'heroicons_outline:clipboard-check',
            link: `/party/events/${eventId}/registrations`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Roadmap/Guião
        tabItems.push({
            id: `event-${eventId}-roadmap`,
            title: 'Guião / Tarefas',
            type: 'basic',
            icon: 'heroicons_outline:map',
            link: `/party/events/${eventId}/roadmap`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        return tabItems;
    }
}